import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { marketingAPI } from '../lib/api';
import { Button } from '../components/ui/button';

export default function LandingPageView() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    marketingAPI.viewLandingPage(slug)
      .then(r => setPage(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" /></div>;
  if (!page) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-center"><p className="text-zinc-500 text-lg mb-4">Page not found</p><Link to="/"><Button className="bg-[#b8ff00] text-black font-bold">Go Home</Button></Link></div>
    </div>
  );

  const handleCTAClick = () => {
    marketingAPI.trackLandingClick(slug).catch(() => {});
    if (page.cta_link) window.location.href = page.cta_link;
  };

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="landing-page-view">
      {/* Hero */}
      <div className="bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-950">
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display font-black text-4xl md:text-5xl text-white leading-tight mb-4">
            {page.headline || page.title}
          </motion.h1>
          {page.subheadline && (
            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-lg text-zinc-400 max-w-lg mx-auto mb-8">
              {page.subheadline}
            </motion.p>
          )}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
            <Button onClick={handleCTAClick} className="bg-[#b8ff00] text-black font-bold px-10 py-6 text-lg rounded-full hover:shadow-lg hover:shadow-[#b8ff00]/20 transition-all" data-testid="lp-cta-btn">
              {page.cta_text || 'Get Started'} <ArrowRight size={18} className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Sections */}
      {page.sections?.length > 0 && (
        <div className="max-w-3xl mx-auto px-6 py-12 space-y-8">
          {page.sections.map((section, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
              className="p-6 bg-zinc-900 border border-zinc-800 rounded-2xl">
              <h2 className="font-bold text-xl text-white mb-2">{section.title}</h2>
              <p className="text-zinc-400 leading-relaxed">{section.body}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Footer CTA */}
      <div className="text-center py-12">
        <Button onClick={handleCTAClick} className="bg-[#b8ff00] text-black font-bold px-8 py-5 rounded-full" data-testid="lp-cta-footer-btn">
          {page.cta_text || 'Get Started'} <ArrowRight size={16} className="ml-2" />
        </Button>
        <div className="mt-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-600 hover:text-zinc-400">
            <div className="w-4 h-4 bg-gradient-to-br from-[#b8ff00] to-[#00ff88] rounded flex items-center justify-center">
              <span className="text-[8px] font-bold text-black">M</span>
            </div>
            Built with MMarketing OS
          </Link>
        </div>
      </div>
    </div>
  );
}
